"use client";

import { Suspense, useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { useSignUp } from "@clerk/nextjs/legacy";
import { useUser } from "@clerk/nextjs";
import { CheckCircle2, Eye, EyeOff, Loader2 } from "lucide-react";
import { clerkErrorToUserMessage, type AuthErrorMessage } from "@/lib/auth/clerk-error-message";

const benefits = [
  "Build landing pages in under 60 seconds",
  "5 proven, high-converting templates",
  "Built-in lead capture & analytics",
  "Publish to your own subdomain instantly",
];

function SignUpForm() {
  const { isLoaded, signUp, setActive } = useSignUp();
  const { isSignedIn } = useUser();
  const router = useRouter();
  const searchParams = useSearchParams();
  const customerId = searchParams.get("customer_id");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [code, setCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<AuthErrorMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [linking, setLinking] = useState(false);

  // PHU-225: a user who already has a Clerk session and lands on /sign-up
  // (e.g. clicked a stale link) should bounce to /dashboard, not get
  // shown an empty sign-up form. BUT we must skip this auto-redirect
  // during an in-flight sign-up — otherwise setActive flips isSignedIn
  // true mid-handleVerify and we race past `router.push("/welcome")`
  // straight into /dashboard. localSignUpInFlight stays true once a
  // sign-up has started in this tab; the verify/oauth handler owns
  // navigation from that point on.
  const localSignUpInFlight = useRef(false);
  useEffect(() => {
    if (isSignedIn && !localSignUpInFlight.current) {
      router.replace("/dashboard");
    }
  }, [isSignedIn, router]);

  useEffect(() => {
    if (!customerId) return;
    fetch(`/api/checkout/customer?id=${encodeURIComponent(customerId)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.email) setEmail(data.email);
      })
      .catch(() => {});
  }, [customerId]);

  // PHU-224: log raw at warn level (Next.js 16 elevates console.error to
  // its dev overlay — expected auth failures shouldn't trip that).
  function reportError(
    err: unknown,
    source: "email" | "verify" | "oauth",
  ) {
    console.warn(`[sign-up:${source}] Clerk error`, err);
    setError(clerkErrorToUserMessage(err));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isLoaded) return;
    setError(null);
    setLoading(true);
    localSignUpInFlight.current = true;

    try {
      await signUp.create({
        emailAddress: email,
        password,
        firstName: firstName || undefined,
      });

      await signUp.prepareEmailAddressVerification({
        strategy: "email_code",
      });

      setVerifying(true);
    } catch (err) {
      reportError(err, "email");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!isLoaded) return;
    setError(null);
    setLoading(true);
    localSignUpInFlight.current = true;

    try {
      const result = await signUp.attemptEmailAddressVerification({
        code,
      });

      if (result.status === "complete" && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });

        // Link the Autumn subscription. A 504 means Convex hasn't synced
        // yet — retry is safe. We deliberately don't forward the URL
        // `customer_id` — the link route resolves the customer via the
        // user's Clerk-verified email, which prevents an attacker who
        // learned someone else's customer_id from binding it to their
        // fresh account.
        setLinking(true);
        try {
          const res = await fetch("/api/checkout/link", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
          });
          if (!res.ok && res.status !== 400) {
            const data = (await res.json().catch(() => ({}))) as {
              reason?: string;
            };
            setError({
              title:
                data.reason === "user-not-synced"
                  ? "We're still setting up your account"
                  : "We couldn't attach your plan automatically",
              detail:
                data.reason === "user-not-synced"
                  ? "Give it a minute, then head to settings — your plan will appear there."
                  : "Visit Settings in a minute and it should pick up automatically.",
            });
          }
        } catch (linkErr) {
          console.warn("[sign-up:verify] /api/checkout/link failed", linkErr);
          setError({
            title: "Network hiccup attaching your plan",
            detail: "Visit Settings in a minute if your plan doesn't appear.",
          });
        } finally {
          setLinking(false);
        }

        router.push("/welcome");
        return;
      }

      // Status not complete — Clerk wants more verification (rare with
      // the email-code flow but possible). Show a friendly hint.
      setError({
        title: "We couldn't complete sign-up",
        detail: "Try again, or contact support if it keeps happening.",
      });
    } catch (err) {
      // PHU-225: silent-success path. If Clerk created the session
      // before throwing on a downstream step, finish the sign-up.
      if (signUp?.status === "complete" && signUp.createdSessionId) {
        try {
          await setActive({ session: signUp.createdSessionId });
          router.push("/welcome");
          return;
        } catch (innerErr) {
          console.warn("[sign-up:verify] setActive after silent-complete threw", innerErr);
        }
      }
      reportError(err, "verify");
    } finally {
      setLoading(false);
    }
  }

  async function handleOAuth(strategy: "oauth_google" | "oauth_github") {
    if (!isLoaded) return;
    setError(null);
    localSignUpInFlight.current = true;
    try {
      // Don't forward customer_id through the OAuth redirect or as Clerk
      // unsafeMetadata. unsafeMetadata is documented as user-writable and
      // attacker-controllable, and the URL param is just as untrustworthy.
      // /welcome resolves the Autumn customer via the Clerk-verified email
      // server-side after sign-up completes.
      await signUp.authenticateWithRedirect({
        strategy,
        redirectUrl: "/sign-up/sso-callback",
        redirectUrlComplete: "/welcome",
      });
    } catch (err) {
      if (signUp?.status === "complete" && signUp.createdSessionId) {
        try {
          await setActive({ session: signUp.createdSessionId });
          router.push("/welcome");
          return;
        } catch (innerErr) {
          console.warn("[sign-up:oauth] setActive after silent-complete threw", innerErr);
        }
      }
      reportError(err, "oauth");
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between bg-[#0F172A] p-12">
        <div>
          <Link href="/" className="flex items-center">
            <Image
              src="/funnlr_logo.png"
              alt="Funnlr"
              width={300}
              height={72}
              className="h-40 w-auto brightness-0 invert"
            />
          </Link>

          <div className="mt-16">
            <h1 className="font-heading text-3xl font-bold text-white">
              Start building funnels
              <br />
              that convert.
            </h1>
            <p className="mt-4 text-base leading-relaxed text-slate-400">
              Turn your offers into high-converting landing pages in
              seconds.
            </p>
          </div>

          <ul className="mt-10 flex flex-col gap-4">
            {benefits.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-500" />
                <span className="text-sm text-slate-300">{b}</span>
              </li>
            ))}
          </ul>
        </div>

      </div>

      {/* Right form panel */}
      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <Link href="/" className="flex items-center">
              <Image
                src="/funnlr_logo.png"
                alt="Funnlr"
                width={120}
                height={32}
                className="h-8 w-auto"
              />
            </Link>
          </div>

          {!verifying ? (
            <>
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                Create your account
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Get started for free — no credit card required.
              </p>

              {/* OAuth buttons */}
              <div className="mt-6 flex flex-col gap-2.5">
                <button
                  type="button"
                  onClick={() => handleOAuth("oauth_google")}
                  className="flex w-full items-center justify-center gap-2.5 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continue with Google
                </button>
              </div>

              <div className="my-5 flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs text-slate-400">or</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>

              {/* Email + password form */}
              <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
                <div>
                  <label htmlFor="firstName" className="mb-1 block text-xs font-medium text-slate-700">
                    Name
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Jane Smith"
                    className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1 block text-xs font-medium text-slate-700">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jane@example.com"
                    className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="mb-1 block text-xs font-medium text-slate-700">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min 8 characters"
                      className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 pr-10 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {error && !error.silent && (
                  <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                    <p className="font-medium">{error.title}</p>
                    {error.detail && <p className="mt-0.5 text-red-600">{error.detail}</p>}
                    {error.action && (
                      error.action.flow === "oauth_google" ? (
                        <button
                          type="button"
                          onClick={() => handleOAuth("oauth_google")}
                          className="mt-2 inline-flex items-center text-sm font-semibold text-red-800 underline underline-offset-2 hover:text-red-900"
                        >
                          {error.action.label} →
                        </button>
                      ) : error.action.href ? (
                        <Link
                          href={error.action.href}
                          className="mt-2 inline-flex items-center text-sm font-semibold text-red-800 underline underline-offset-2 hover:text-red-900"
                        >
                          {error.action.label} →
                        </Link>
                      ) : null
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-1 flex w-full items-center justify-center gap-2 rounded-lg bg-brand-500 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-60"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {loading ? "Creating account..." : "Create account"}
                </button>

                <div id="clerk-captcha" />
              </form>

              <p className="mt-5 text-center text-sm text-slate-500">
                Already have an account?{" "}
                <Link
                  href="/sign-in"
                  className="font-medium text-brand-500 hover:text-brand-600"
                >
                  Log in
                </Link>
              </p>

              <p className="mt-4 text-center text-xs leading-relaxed text-slate-400">
                By creating an account you agree to our{" "}
                <Link href="/terms" className="underline hover:text-slate-600">Terms</Link>
                {" "}and{" "}
                <Link href="/privacy" className="underline hover:text-slate-600">Privacy Policy</Link>.
              </p>
            </>
          ) : (
            <>
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                Check your email
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                We sent a 6-digit code to{" "}
                <span className="font-medium text-slate-700">{email}</span>
              </p>

              <form onSubmit={handleVerify} className="mt-6 flex flex-col gap-3.5">
                <div>
                  <label htmlFor="code" className="mb-1 block text-xs font-medium text-slate-700">
                    Verification code
                  </label>
                  <input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="123456"
                    className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-center text-lg font-semibold tracking-[0.3em] text-slate-900 placeholder:text-slate-400 placeholder:tracking-[0.3em] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                  />
                </div>

                {error && !error.silent && (
                  <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                    <p className="font-medium">{error.title}</p>
                    {error.detail && <p className="mt-0.5 text-red-600">{error.detail}</p>}
                    {error.action && (
                      error.action.flow === "oauth_google" ? (
                        <button
                          type="button"
                          onClick={() => handleOAuth("oauth_google")}
                          className="mt-2 inline-flex items-center text-sm font-semibold text-red-800 underline underline-offset-2 hover:text-red-900"
                        >
                          {error.action.label} →
                        </button>
                      ) : error.action.href ? (
                        <Link
                          href={error.action.href}
                          className="mt-2 inline-flex items-center text-sm font-semibold text-red-800 underline underline-offset-2 hover:text-red-900"
                        >
                          {error.action.label} →
                        </Link>
                      ) : null
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || linking}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-500 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-60"
                >
                  {(loading || linking) && <Loader2 className="h-4 w-4 animate-spin" />}
                  {linking
                    ? "Setting up your account..."
                    : loading
                      ? "Verifying..."
                      : "Verify email"}
                </button>
              </form>

              <button
                type="button"
                onClick={() => { setVerifying(false); setCode(""); setError(null); }}
                className="mt-4 text-sm text-slate-500 hover:text-slate-700"
              >
                &larr; Use a different email
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SignUpPage() {
  return (
    <Suspense>
      <SignUpForm />
    </Suspense>
  );
}
